import React from 'react';
import scheduleService from '../../services/scheduleService';
import positionsService from '../../services/positionsService';
import exitService from '../../services/exitService';
import terminateService, {
  TerminateRequest, TerminateResponse,
} from '../../services/terminateService';
import type {
  ScheduleDetailResponse,
  ScheduleRow,
  ScheduleStrategySummary,
  SchedulePositionsResponse,
  ScheduleSummaryResponse,
  ScheduleViewMode,
} from '../../types/schedule';
import { getTodayIST } from '../../utils/dateUtils';
import { POSITION_STATUS, ENTRY_STATUS } from '../../constants/tradingConstants';
import { useActingAs } from '../../context/ActingAsContext';
import { useToast } from '../common/ToastContainer';
import { useConfirm } from '../common/useConfirm';
import {
  retryPositionBody,
  exitPositionBody,
  strategyBulkBody,
  basketBulkBody,
} from './shared/confirmBodies';

/** View tab — Phase B3 widens to include 'positions'. */
export type ScheduleView = 'classic' | 'pro' | 'positions';

const POLL_INTERVAL_MS = 30_000;

export type GroupKey = string;

export interface DetailCacheEntry {
  rows: ScheduleRow[];
  fingerprint: string;
  fetchedAt: number;
}

/** Fingerprint that captures the fields where a real user-visible change can happen. */
export function fingerprintOf(s: ScheduleStrategySummary): string {
  const e = s.entry_counts;
  const x = s.exit_counts;
  const sl = s.sl_counts;
  return [
    e.complete, e.rejected, e.cancelled, e.pending, e.open, e.none,
    x.complete, x.rejected, x.cancelled, x.pending, x.open, x.none,
    sl.placed, sl.rejected, sl.pending, sl.open, sl.not_needed,
    s.open_count, s.closed_count,
    s.exit_slot_open_count, s.exit_slot_closed_count, s.carry_open_count,
    s.total_pnl.toFixed(2),
  ].join('|');
}

export function keyOf(s: { basket_id: string; strategy_id: string }): GroupKey {
  return `${s.basket_id}#${s.strategy_id}`;
}

/**
 * Shared data layer for Schedule views. Encapsulates:
 *   - summary fetch + 30s polling when viewing today
 *   - 3-way Mode (default / entries / full) — reshapes scope across views
 *   - lazy detail fetch per strategy with fingerprint-driven cache invalidation
 *   - modal state (opened strategy, opened basket, breadcrumb, edit launcher target)
 *
 * Classic and Pro views consume the same hook — only rendering differs.
 */
/**
 * Optional lazy initializers so callers (SchedulePage / BasketScheduleView)
 * can hydrate `view` and `viewMode` from URL / localStorage in a SINGLE step
 * — avoids the previous pattern of `useState(default) + useEffect(setX)` which
 * caused a redundant first fetch with the wrong scope before the push effect
 * re-fired fetchSummary with the desired one. Each initializer is a function
 * to match `useState`'s lazy-init contract.
 */
export interface UseScheduleDataOpts {
  initialView?: () => ScheduleView;
  initialViewMode?: () => ScheduleViewMode;
}

export function useScheduleData(opts?: UseScheduleDataOpts) {
  const today = React.useMemo(() => getTodayIST(), []);

  const { showError, showSuccess, showWarning } = useToast();
  const { askConfirm, confirmDialog } = useConfirm();

  const [selectedDate, setSelectedDate] = React.useState(today);
  const [viewMode, setViewMode] = React.useState<ScheduleViewMode>(
    opts?.initialViewMode ?? (() => 'default'),
  );
  // Phase B3: page-level view tab. 'positions' fetches from /execution-schedule
  // with format=positions; the others fetch format=summary. Lives in the hook
  // (not just the page) so dependent effects can dispatch the right fetch.
  const [view, setViewState] = React.useState<ScheduleView>(
    opts?.initialView ?? (() => 'classic'),
  );
  // Phase B3: page-level search query — applies the same predicate across
  // all three views (strategy_name + basket_name + symbol + client_id
  // substring match). Each view reads this from the bundle instead of
  // owning its own search state.
  const [pageSearchQuery, setPageSearchQuery] = React.useState('');
  // Phase 3.5: when admin switches "View as", the resolved query-string
  // changes silently in localStorage. Including `actingAs` in fetchSummary's
  // deps causes an immediate re-fetch (instead of waiting for the 30s poll).
  const { actingAs } = useActingAs();

  const [payload, setPayload] = React.useState<ScheduleSummaryResponse | null>(null);
  const [positionsPayload, setPositionsPayload] = React.useState<SchedulePositionsResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const hasLoadedOnceRef = React.useRef(false);

  const [openedStrategyKey, setOpenedStrategyKey] = React.useState<GroupKey | null>(null);
  const [openedBasketId, setOpenedBasketId] = React.useState<string | null>(null);
  const [breadcrumbBasketId, setBreadcrumbBasketId] = React.useState<string | null>(null);
  const [editTarget, setEditTarget] = React.useState<{ strategyId: string; basketId: string } | null>(null);

  const detailCacheRef = React.useRef<Map<GroupKey, DetailCacheEntry>>(new Map());
  const [detailLoading, setDetailLoading] = React.useState<GroupKey | null>(null);
  const [, forceUpdate] = React.useReducer((n) => n + 1, 0);

  // Stale-response race protection. Each fetch (summary, detail) captures a
  // version on entry and re-checks it at await-resume. With Mode + Date both
  // mutable, two requests can be in flight simultaneously; without the gate,
  // a slower response could land after the user has already moved on and
  // overwrite the newer payload. Standard React pattern.
  const latestRequestVersionRef = React.useRef(0);

  // Per-position action state — mirrors the Positions page pattern so action
  // buttons can show a disabled/loading state while a call is in flight.
  const [retryingId, setRetryingId] = React.useState<string | null>(null);
  const [exitingId, setExitingId] = React.useState<string | null>(null);
  const [bulkActionPending, setBulkActionPending] = React.useState<boolean>(false);
  const [terminatingId, setTerminatingId] = React.useState<string | null>(null);
  // Open ConfirmTerminateDialog (rendered by the views) — null when closed.
  const [terminateReq, setTerminateReq] = React.useState<TerminateRequest | null>(null);
  const [terminateLabel, setTerminateLabel] = React.useState<string>('');

  const isToday = selectedDate === today;
  const isPast = selectedDate < today;

  const fetchSummary = React.useCallback(async () => {
    if (!hasLoadedOnceRef.current) setLoading(true);
    else setRefreshing(true);
    setError(null);
    const version = ++latestRequestVersionRef.current;
    try {
      if (view === 'positions') {
        const data = await scheduleService.getPositions(selectedDate, viewMode);
        if (version !== latestRequestVersionRef.current) return; // stale, drop
        setPositionsPayload(data);
        hasLoadedOnceRef.current = true;
      } else {
        const data = await scheduleService.getScheduleSummary(selectedDate, viewMode);
        if (version !== latestRequestVersionRef.current) return; // stale, drop
        const nextKeys = new Set<GroupKey>();
        for (const s of data.strategies) nextKeys.add(keyOf(s));
        for (const k of Array.from(detailCacheRef.current.keys())) {
          if (!nextKeys.has(k)) detailCacheRef.current.delete(k);
        }
        setPayload(data);
        hasLoadedOnceRef.current = true;
      }
    } catch (e: any) {
      if (version !== latestRequestVersionRef.current) return; // stale, drop
      setError(e?.message || 'Failed to load execution schedule');
      if (!hasLoadedOnceRef.current) {
        if (view === 'positions') setPositionsPayload(null);
        else setPayload(null);
      }
    } finally {
      if (version === latestRequestVersionRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, viewMode, view, actingAs]);

  React.useEffect(() => {
    // Date OR mode OR view change invalidates the detail cache: cached leg
    // rows were fetched against a different scope and would be silently
    // wrong. View change closes the open strategy modal too (handled via
    // setView below).
    hasLoadedOnceRef.current = false;
    detailCacheRef.current.clear();
    fetchSummary();
  }, [fetchSummary]);

  // useMemo on both derivations so the `?? []` fallback returns a stable
  // array reference between renders — otherwise every render produces a
  // fresh `[]` and any downstream useCallback that lists `strategies` /
  // `positions` in its dep array re-creates on every render (lint:
  // react-hooks/exhaustive-deps).
  const strategies = React.useMemo(
    () => payload?.strategies ?? [],
    [payload],
  );
  const totals = payload?.totals ?? null;
  // Flat positions list from format=positions — populated only when
  // view='positions'. Empty for Classic/Pro views.
  const positions = React.useMemo(
    () => positionsPayload?.positions ?? [],
    [positionsPayload],
  );

  // Poll today's view always; poll past dates only when the response still
  // carries live exposure (e.g. a POSITIONAL leg entered on the viewed day
  // and still OPEN today), so unrealized P&L keeps moving with LTP. Past
  // days with only CLOSED rows skip polling — values are frozen.
  // For the Positions view, derive live exposure from the flat positions
  // list (no summary totals to lean on).
  const hasLivePositions = view === 'positions'
    ? positions.some((p) =>
        p.position_status === POSITION_STATUS.OPEN
        || p.position_status === POSITION_STATUS.EXIT_PENDING
        || p.position_status === POSITION_STATUS.ENTRY_PENDING)
    : ((totals?.open_count ?? 0) > 0 || (totals?.pending ?? 0) > 0);
  const shouldPoll = isToday || (isPast && hasLivePositions);

  React.useEffect(() => {
    if (!shouldPoll) return;
    const id = window.setInterval(fetchSummary, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [shouldPoll, fetchSummary]);

  const summaryByKey = React.useMemo(() => {
    const m = new Map<GroupKey, ScheduleStrategySummary>();
    for (const s of strategies) m.set(keyOf(s), s);
    return m;
  }, [strategies]);

  const fetchDetail = React.useCallback(async (groupKey: GroupKey, force = false) => {
    // Detail fetches are NOT version-gated — multiple groupKeys can be in
    // flight concurrently (e.g. retryBasketRejections fans out fetchDetail
    // across every strategy in the basket). Stale-data concerns for detail
    // are handled by the per-key fingerprint check below + cache eviction
    // on summary refresh.
    const summary = summaryByKey.get(groupKey);
    if (!summary) return;
    const cached = detailCacheRef.current.get(groupKey);
    const currentFp = fingerprintOf(summary);
    if (!force && cached && cached.fingerprint === currentFp) return;

    setDetailLoading(groupKey);
    try {
      const data: ScheduleDetailResponse = await scheduleService.getStrategyDetail(
        selectedDate,
        summary.basket_id,
        summary.strategy_id,
        viewMode,
      );
      detailCacheRef.current.set(groupKey, {
        rows: data.rows,
        fingerprint: currentFp,
        fetchedAt: Date.now(),
      });
      forceUpdate();
    } catch (e) {
      console.error('Strategy detail fetch failed', e);
    } finally {
      setDetailLoading((k) => (k === groupKey ? null : k));
    }
  }, [selectedDate, viewMode, summaryByKey]);

  React.useEffect(() => {
    if (openedStrategyKey) fetchDetail(openedStrategyKey);
  }, [openedStrategyKey, fetchDetail]);

  // ── Modal transitions (shared between views) ──────────────────────────

  const openStrategyFromBasket = React.useCallback((groupKey: GroupKey) => {
    setBreadcrumbBasketId(openedBasketId);
    setOpenedBasketId(null);
    setOpenedStrategyKey(groupKey);
  }, [openedBasketId]);

  const closeStrategyModal = React.useCallback(() => {
    setOpenedStrategyKey(null);
    setBreadcrumbBasketId(null);
  }, []);

  // View-change closes the open strategy modal AND the open basket modal.
  // Without this, switching from Pro (modal open) → Positions would leave
  // the modal stranded rendering Pro-shaped data over a Positions table.
  // The detail-cache clear in the fetchSummary-trigger effect handles the
  // data side; this handles the open-key state.
  const setView = React.useCallback((next: ScheduleView) => {
    setViewState(next);
    setOpenedStrategyKey(null);
    setOpenedBasketId(null);
    setBreadcrumbBasketId(null);
  }, []);

  const backToBasket = breadcrumbBasketId
    ? () => {
        setOpenedStrategyKey(null);
        setOpenedBasketId(breadcrumbBasketId);
        setBreadcrumbBasketId(null);
      }
    : undefined;

  const modalGroupKey = openedStrategyKey;
  const modalSummary = modalGroupKey ? summaryByKey.get(modalGroupKey) ?? null : null;
  const modalRows = modalGroupKey
    ? detailCacheRef.current.get(modalGroupKey)?.rows ?? []
    : [];

  /** Refresh the summary + the currently-open strategy's detail (cache bust). */
  const refreshAfterAction = React.useCallback(async () => {
    await fetchSummary();
    if (modalGroupKey) await fetchDetail(modalGroupKey, true);
  }, [fetchSummary, fetchDetail, modalGroupKey]);

  /**
   * Resolve the ScheduleRow for a given positionId from the currently-open
   * strategy's cached detail. The row is captured synchronously inside each
   * action handler before any `await`, so there's no race with the modal
   * closing. A `null` return means the cache is cold (detail not yet fetched
   * for this strategy) — callers fall back to a plain-text confirm body.
   */
  const findRowById = React.useCallback((positionId: string): ScheduleRow | null => {
    if (!modalGroupKey) return null;
    const rows = detailCacheRef.current.get(modalGroupKey)?.rows ?? [];
    return rows.find((r) => r.position_id === positionId) ?? null;
  }, [modalGroupKey]);

  /**
   * Summarise a batch of Promise.allSettled results into a success / warning
   * / error toast so the user sees exactly how the bulk submission landed,
   * even when some per-position calls failed.
   */
  const reportBulkOutcome = React.useCallback((
    results: PromiseSettledResult<unknown>[],
    subject: string,  // e.g. "Retry"
  ) => {
    const total = results.length;
    const fulfilled = results.filter((r) => r.status === 'fulfilled').length;
    const rejected = total - fulfilled;
    const unit = total === 1 ? 'position' : 'positions';
    // Phase 6: count deferred / already_placed among fulfilled retries so the
    // bulk-retry toast doesn't count them as "submitted" broker calls.
    const fulfilledDeferred = results.filter(
      (r) => r.status === 'fulfilled'
        && (r as PromiseFulfilledResult<any>).value?.deferred === true,
    ).length;
    const fulfilledAlreadyPlaced = results.filter(
      (r) => r.status === 'fulfilled'
        && (r as PromiseFulfilledResult<any>).value?.already_placed === true,
    ).length;
    const trulySubmitted = fulfilled - fulfilledDeferred - fulfilledAlreadyPlaced;
    if (rejected === 0) {
      if (trulySubmitted === 0 && (fulfilledDeferred > 0 || fulfilledAlreadyPlaced > 0)) {
        // Nothing new placed — all rows were already in-flight or filled.
        const parts: string[] = [];
        if (fulfilledDeferred > 0) parts.push(`${fulfilledDeferred} already in progress`);
        if (fulfilledAlreadyPlaced > 0) parts.push(`${fulfilledAlreadyPlaced} already placed`);
        showWarning(`${subject}: ${parts.join(', ')}`);
        return;
      }
      if (fulfilledDeferred > 0 || fulfilledAlreadyPlaced > 0) {
        const skipped = fulfilledDeferred + fulfilledAlreadyPlaced;
        showSuccess(`${subject} submitted for ${trulySubmitted}/${total} ${unit}; ${skipped} skipped (already in progress or placed)`);
        return;
      }
      showSuccess(`${subject} submitted for ${total} ${unit}`);
      return;
    }
    const firstErr =
      (results.find((r) => r.status === 'rejected') as PromiseRejectedResult | undefined)
        ?.reason?.message ?? 'Unknown error';
    if (fulfilled === 0) {
      showError(`All ${total} ${unit} failed: ${firstErr}`);
    } else {
      showWarning(`${subject} submitted for ${trulySubmitted}/${total}; ${rejected} failed: ${firstErr}`);
    }
  }, [showSuccess, showWarning, showError]);

  /**
   * Phase 6: report the outcome of an exit API call, folding the
   * `entry_placing_deferred` list (Phase 5) into the success toast so the
   * user knows when a subset of positions couldn't be exited because their
   * entry claim was mid-flight. Returns nothing — always shows exactly one
   * toast (success, warning, or mixed).
   */
  const reportExitOutcome = React.useCallback((
    result: { exits_placed: number; total_positions: number; exits_deferred?: number; entry_placing_deferred?: Array<{ position_id: string; reason: string }> },
    successMessage: string,
  ) => {
    // Two distinct deferral buckets, both mean "leg is not out yet, retry":
    //  - entry_placing_deferred: unfilled entry claim mid-flight (Phase 5)
    //  - exits_deferred: filled OPEN leg whose SL claim was mid-flight at exit
    // Both clear within seconds; surface a combined retry nudge instead of a
    // plain success toast (which would hide a still-open leg — the Z72372 bug).
    const entryDeferred = result?.entry_placing_deferred?.length ?? 0;
    const exitDeferred = result?.exits_deferred ?? 0;
    const deferredCount = entryDeferred + exitDeferred;
    if (deferredCount === 0) {
      showSuccess(successMessage);
      return;
    }
    // Distinct verbs per bucket: an unfilled entry claim is "still placing";
    // a filled leg whose SL claim was mid-flight is "still settling". Both
    // clear within seconds and want the same "try again" nudge.
    const parts: string[] = [];
    if (entryDeferred > 0) parts.push(`${entryDeferred} still placing`);
    if (exitDeferred > 0) parts.push(`${exitDeferred} still settling`);
    const deferredMsg = `${parts.join(', ')} — try again in a moment`;
    if (result.exits_placed > 0) {
      showWarning(`${successMessage} (${deferredCount} skipped: ${deferredMsg})`);
    } else {
      showWarning(deferredMsg);
    }
  }, [showSuccess, showWarning]);

  const retryPosition = React.useCallback(async (positionId: string) => {
    const row = findRowById(positionId);
    const ok = await askConfirm({
      title: 'Retry Entry',
      message: row ? retryPositionBody(row) : 'Retry entry for this position?',
      confirmText: 'Retry',
      variant: 'warning',
      size: row ? 'lg' : 'md',
    });
    if (!ok) return;
    setRetryingId(positionId);
    try {
      const result = await positionsService.retryPosition(positionId);
      // Phase 6: distinguish claim-in-flight and already-placed friendly
      // paths from the normal "retry submitted" so the user doesn't see a
      // false green tick for a placement that never fired.
      if (result?.deferred) {
        showWarning('Entry placement already in progress — please wait');
      } else if (result?.already_placed) {
        showWarning('Entry already placed for this position');
      } else {
        showSuccess('Retry submitted');
      }
      await refreshAfterAction();
    } catch (e: any) {
      showError(e?.message || 'Failed to retry position');
    } finally {
      setRetryingId((id) => (id === positionId ? null : id));
    }
  }, [findRowById, askConfirm, showSuccess, showWarning, showError, refreshAfterAction]);

  const exitPosition = React.useCallback(async (positionId: string) => {
    const row = findRowById(positionId);
    const isPending = row?.position_status === 'ENTRY_PENDING' && row?.entry_status !== 'REJECTED';
    const ok = await askConfirm({
      title: isPending ? 'Cancel Pending Entry' : 'Exit Position',
      message: row ? exitPositionBody(row) : 'Exit this position? This will place an exit order immediately.',
      confirmText: isPending ? 'Cancel Order' : 'Exit',
      variant: 'danger',
      size: row ? 'lg' : 'md',
    });
    if (!ok) return;
    setExitingId(positionId);
    try {
      const result = await exitService.exitSingle(positionId);
      reportExitOutcome(result, isPending ? 'Cancellation submitted' : 'Exit submitted');
      await refreshAfterAction();
    } catch (e: any) {
      showError(e?.message || 'Failed to exit position');
    } finally {
      setExitingId((id) => (id === positionId ? null : id));
    }
  }, [findRowById, askConfirm, reportExitOutcome, showError, refreshAfterAction]);

  const retryPositionsBulk = React.useCallback(async (positionIds: string[]) => {
    if (positionIds.length === 0) return;
    // Pull the matching rows from the open strategy modal's cache so the
    // dialog can show each rejection with its broker error.
    const rows = modalGroupKey
      ? (detailCacheRef.current.get(modalGroupKey)?.rows ?? [])
          .filter((r) => r.position_id && positionIds.includes(r.position_id))
      : [];
    const hasRows = rows.length > 0;
    const ok = await askConfirm({
      title: 'Retry Rejected Entries',
      message: hasRows
        ? strategyBulkBody({
            strategyName: rows[0]?.strategy_name ?? null,
            basketName: rows[0]?.basket_name ?? null,
            positions: rows,
            action: 'retry',
          })
        : `Retry entry for ${positionIds.length} position${positionIds.length === 1 ? '' : 's'}?`,
      confirmText: `Retry ${positionIds.length}`,
      variant: 'warning',
      size: hasRows ? 'lg' : 'md',
    });
    if (!ok) return;
    setBulkActionPending(true);
    try {
      const results = await Promise.allSettled(
        positionIds.map((id) => positionsService.retryPosition(id)),
      );
      reportBulkOutcome(results, 'Retry');
      await refreshAfterAction();
    } finally {
      setBulkActionPending(false);
    }
  }, [modalGroupKey, askConfirm, reportBulkOutcome, refreshAfterAction]);

  const exitStrategyBulk = React.useCallback(async (strategyId: string) => {
    const summary = strategies.find((s) => s.strategy_id === strategyId) ?? null;
    const rows = modalGroupKey
      ? (detailCacheRef.current.get(modalGroupKey)?.rows ?? [])
          .filter((r) => (r.position_status === 'OPEN' || r.position_status === 'ENTRY_PENDING')
            && r.entry_status !== 'REJECTED'
            && r.position_id)
      : [];
    const hasRows = rows.length > 0;
    const ok = await askConfirm({
      title: 'Exit Strategy',
      message: hasRows
        ? strategyBulkBody({
            strategyName: summary?.strategy_name ?? rows[0]?.strategy_name ?? null,
            basketName: summary?.basket_name ?? rows[0]?.basket_name ?? null,
            positions: rows,
            action: 'exit',
          })
        : 'Exit all open positions in this strategy?',
      confirmText: hasRows ? `Exit ${rows.length}` : 'Exit All',
      variant: 'danger',
      size: hasRows ? 'lg' : 'md',
    });
    if (!ok) return;
    setBulkActionPending(true);
    try {
      const result = await exitService.exitStrategy(strategyId);
      reportExitOutcome(result, 'Strategy exit submitted');
      await refreshAfterAction();
    } catch (e: any) {
      showError(e?.message || 'Failed to exit strategy');
    } finally {
      setBulkActionPending(false);
    }
  }, [strategies, modalGroupKey, askConfirm, reportExitOutcome, showError, refreshAfterAction]);

  /**
   * Exit ALL — single-click stop across the user's entire account:
   *   - Exit every OPEN position via SL-modify.
   *   - Cancel every ENTRY_PENDING entry order.
   *   - Disarm every armed re-entry / TP_REEXEC chain (sets manual_exit=True
   *     so no follow-on trade fires after the click).
   *
   * Eligibility: enabled only when the visible strategies have at least one
   * exitable row — i.e. summed `open_count + entry_counts.pending > 0`.
   * The button caller is responsible for that gate; this handler still
   * defends with a precount + showError so a stale button click is harmless.
   */
  const exitAllBulk = React.useCallback(async () => {
    // Positions view hydrates `positionsPayload` (format=positions) instead of
    // the summary's per-strategy rollup, so derive counts from the flat list
    // when active — otherwise this gate always trips with "no exitable".
    const openTotal = view === 'positions'
      ? positions.reduce(
          (n, p) => n + (p.position_status === POSITION_STATUS.OPEN ? 1 : 0), 0)
      : strategies.reduce((n, s) => n + s.open_count, 0);
    const pendingTotal = view === 'positions'
      ? positions.reduce(
          (n, p) => n + (
            p.position_status === POSITION_STATUS.ENTRY_PENDING
              && p.entry_status !== ENTRY_STATUS.REJECTED ? 1 : 0
          ), 0)
      : strategies.reduce((n, s) => n + s.entry_counts.pending, 0);
    const total = openTotal + pendingTotal;
    if (total === 0) {
      showError('No exitable positions across your strategies.');
      return;
    }
    const ok = await askConfirm({
      title: 'Exit All Positions',
      message:
        `Exit ${openTotal} open position${openTotal !== 1 ? 's' : ''} and ` +
        `cancel ${pendingTotal} pending order${pendingTotal !== 1 ? 's' : ''} ` +
        `across all your strategies? This will also stop any further auto ` +
        `re-entry / re-execute / target-profit re-execute trades for these ` +
        `positions.`,
      confirmText: `Exit ${total}`,
      variant: 'danger',
      size: 'md',
    });
    if (!ok) return;
    setBulkActionPending(true);
    try {
      const result = await exitService.exitAll();
      reportExitOutcome(result, 'Exit-all submitted');
      await refreshAfterAction();
    } catch (e: any) {
      showError(e?.message || 'Failed to exit all positions');
    } finally {
      setBulkActionPending(false);
    }
  }, [view, strategies, positions, askConfirm, reportExitOutcome, showError, refreshAfterAction]);

  /**
   * Exit / cancel an arbitrary id list — drives the Positions tab's
   * multi-select batch bar (cross-strategy selection where the
   * exit-strategy / exit-basket handlers don't fit). Backend's exitService
   * already supports `scope: 'MULTIPLE'` with a `position_ids[]` field,
   * so this is a thin wrapper.
   */
  const exitPositionsBulk = React.useCallback(async (positionIds: string[]) => {
    if (positionIds.length === 0) return;
    const total = positionIds.length;
    const unit = total === 1 ? 'position' : 'positions';
    const ok = await askConfirm({
      title: `Exit ${total} ${total === 1 ? 'Position' : 'Positions'}`,
      message: `Exit ${total} selected ${unit}? This places broker exit / cancel orders immediately.`,
      confirmText: `Exit ${total}`,
      variant: 'danger',
      size: 'md',
    });
    if (!ok) return;
    setBulkActionPending(true);
    try {
      const result = await exitService.exitPositions({ scope: 'MULTIPLE', position_ids: positionIds });
      reportExitOutcome(result, `Exit submitted for ${total} ${unit}`);
      await refreshAfterAction();
    } catch (e: any) {
      showError(e?.message || 'Failed to exit positions');
    } finally {
      setBulkActionPending(false);
    }
  }, [askConfirm, reportExitOutcome, showError, refreshAfterAction]);

  const exitBasketBulk = React.useCallback(async (basketId: string) => {
    const basketStrategies = strategies.filter((s) => s.basket_id === basketId);
    const openTotal = basketStrategies.reduce((n, s) => n + s.open_count, 0);
    if (openTotal === 0) {
      showError('No open positions to exit in this basket.');
      return;
    }
    const basketName = basketStrategies[0]?.basket_name ?? null;
    const ok = await askConfirm({
      title: 'Exit Basket',
      message: basketBulkBody({
        basketName,
        strategies: basketStrategies,
        action: 'exit',
      }),
      confirmText: `Exit ${openTotal}`,
      variant: 'danger',
      size: 'lg',
    });
    if (!ok) return;
    setBulkActionPending(true);
    try {
      const result = await exitService.exitBasket(basketId);
      reportExitOutcome(result, 'Basket exit submitted');
      await refreshAfterAction();
    } catch (e: any) {
      showError(e?.message || 'Failed to exit basket');
    } finally {
      setBulkActionPending(false);
    }
  }, [strategies, askConfirm, reportExitOutcome, showError, refreshAfterAction]);

  /**
   * Retry every rejected entry across all strategies in the basket. Unlike
   * `exitBasket`, there is no single-call API for bulk retry, so this
   * iterates each strategy's detail rows to collect rejected position IDs
   * and fires `retryPosition` per ID.
   */
  const retryBasketRejections = React.useCallback(async (basketId: string) => {
    const basketStrategies = strategies.filter((s) => s.basket_id === basketId);
    const rejectedTotal = basketStrategies.reduce(
      (sum, s) => sum + s.entry_counts.rejected, 0,
    );
    if (rejectedTotal === 0) {
      showError('No rejected positions to retry in this basket.');
      return;
    }
    const basketName = basketStrategies[0]?.basket_name ?? null;
    const ok = await askConfirm({
      title: 'Retry Basket Rejections',
      message: basketBulkBody({
        basketName,
        strategies: basketStrategies,
        action: 'retry',
      }),
      confirmText: `Retry ${rejectedTotal}`,
      variant: 'warning',
      size: 'lg',
    });
    if (!ok) return;

    setBulkActionPending(true);
    try {
      // Fetch detail for every strategy (cache hits are near-free) so we
      // can collect the per-leg position IDs that need retrying.
      await Promise.all(basketStrategies.map((s) => fetchDetail(keyOf(s), false)));
      const positionIds: string[] = [];
      for (const s of basketStrategies) {
        const cached = detailCacheRef.current.get(keyOf(s));
        if (!cached) continue;
        for (const row of cached.rows) {
          if (row.entry_status === 'REJECTED' && row.position_id) {
            positionIds.push(row.position_id);
          }
        }
      }
      if (positionIds.length === 0) {
        showError('No retryable positions found after refreshing.');
        return;
      }
      const results = await Promise.allSettled(
        positionIds.map((id) => positionsService.retryPosition(id)),
      );
      reportBulkOutcome(results, 'Retry');
      await refreshAfterAction();
    } finally {
      setBulkActionPending(false);
    }
  }, [strategies, fetchDetail, refreshAfterAction, askConfirm, reportBulkOutcome, showError]);

  // ── Terminate (platform-only "stop watching"; NEVER calls the broker) ──
  // Unlike Exit, terminate uses the dedicated ConfirmTerminateDialog (mounted
  // by the views) so it can fetch the authoritative in-scope count via a
  // dry_run, capture an optional reason, and gate ALL behind a typed word.
  // The ask* handlers just open the dialog; onTerminateConfirm runs the write.
  const reportTerminateOutcome = React.useCallback((res: TerminateResponse) => {
    const n = res.terminated_count;
    const parts = [`Terminated ${n} position${n === 1 ? '' : 's'}`];
    if (res.chains_disarmed) parts.push(`disarmed ${res.chains_disarmed} chain${res.chains_disarmed === 1 ? '' : 's'}`);
    if (res.skipped_version_conflicts) parts.push(`${res.skipped_version_conflicts} updating (retry)`);
    const msg = parts.join(', ');
    if (res.failed) showWarning(`${msg}, ${res.failed} failed — see logs`);
    else showSuccess(msg);
  }, [showSuccess, showWarning]);

  const askTerminatePosition = React.useCallback((positionId: string) => {
    const row = findRowById(positionId);
    setTerminateLabel(row?.symbol ? `this leg (${row.symbol})` : 'this position');
    setTerminateReq({ scope: 'SINGLE', position_ids: [positionId] });
  }, [findRowById]);

  const askTerminateStrategy = React.useCallback((strategyId: string) => {
    const s = strategies.find((x) => x.strategy_id === strategyId) ?? null;
    setTerminateLabel(s?.strategy_name ? `strategy "${s.strategy_name}"` : 'this strategy');
    setTerminateReq({ scope: 'STRATEGY', strategy_id: strategyId });
  }, [strategies]);

  const askTerminateBasket = React.useCallback((basketId: string) => {
    const s = strategies.find((x) => x.basket_id === basketId) ?? null;
    setTerminateLabel(s?.basket_name ? `basket "${s.basket_name}"` : 'this basket');
    setTerminateReq({ scope: 'BASKET', basket_id: basketId });
  }, [strategies]);

  const askTerminateAll = React.useCallback(() => {
    setTerminateLabel('your entire book');
    setTerminateReq({ scope: 'ALL' });
  }, []);

  const closeTerminate = React.useCallback(() => setTerminateReq(null), []);

  // Dispatches by the request's scope. Wired scopes today: SINGLE (per-leg) and
  // ALL (toolbar). STRATEGY/BASKET/ACCOUNT are supported by the service +
  // dialog and can be surfaced by adding an ask* trigger — no change here.
  const onTerminateConfirm = React.useCallback(async (reason: string) => {
    const req = terminateReq;
    if (!req) return;
    const single = req.scope === 'SINGLE';
    if (single) setTerminatingId(req.position_ids?.[0] ?? null);
    else setBulkActionPending(true);
    try {
      const res: TerminateResponse = single
        ? await terminateService.terminateSingle(req.position_ids![0], reason)
        : await terminateService.terminatePositions({ ...req, reason });
      reportTerminateOutcome(res);
      setTerminateReq(null);
      await refreshAfterAction();
    } catch (e: any) {
      showError(e?.message || 'Failed to terminate positions');
    } finally {
      if (single) setTerminatingId(null); else setBulkActionPending(false);
    }
  }, [terminateReq, reportTerminateOutcome, refreshAfterAction, showError]);

  return {
    // state
    today, selectedDate, setSelectedDate,
    viewMode, setViewMode,
    view, setView,
    pageSearchQuery, setPageSearchQuery,
    payload, strategies, totals,
    positionsPayload, positions,
    loading, refreshing, error,
    isToday, isPast,
    // scope gate — single source of truth for action eligibility
    isBrokerScope: viewMode === 'default',
    canAct: isToday && viewMode === 'default',
    // modal state
    openedStrategyKey, setOpenedStrategyKey,
    openedBasketId, setOpenedBasketId,
    breadcrumbBasketId,
    editTarget, setEditTarget,
    modalSummary, modalRows, modalGroupKey,
    detailLoading,
    // action state
    retryingId, exitingId, bulkActionPending, terminatingId,
    // handlers
    fetchSummary, fetchDetail,
    retryPosition, exitPosition,
    retryPositionsBulk, exitStrategyBulk, exitBasketBulk, retryBasketRejections,
    exitPositionsBulk,
    exitAllBulk,
    // terminate handlers (open ConfirmTerminateDialog) + its wiring
    askTerminatePosition, askTerminateStrategy, askTerminateBasket, askTerminateAll,
    terminateRequest: terminateReq,
    terminateScopeLabel: terminateLabel,
    closeTerminate, onTerminateConfirm,
    openStrategyFromBasket, closeStrategyModal, backToBasket,
    // in-app confirm dialog — mount once at the view level
    confirmDialog,
    currentIstHms: (view === 'positions'
      ? positionsPayload?.current_ist_time
      : payload?.current_ist_time) ?? null,
  };
}

export type ScheduleDataBundle = ReturnType<typeof useScheduleData>;
